/**
 * Security — credentials and secret definitions in one place.
 *
 * Three surfaces that were three nav items: service tokens, the platform-wide
 * secret values, and the secret field catalogue. They are one concept split
 * by lifecycle stage, not three concepts.
 *
 * Tab order is by how often an operator touches them, not by the order the
 * data model builds them up. Rotation is the recurring job and already
 * generates its own triage item; the field catalogue is set up once and then
 * left alone for months.
 */
import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { ListShape } from '../../ui/shapes.js';
import { SectionHeader } from '../../ui/SectionHeader.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { GlobalSecretsCard } from '../secret-fields/GlobalSecretsCard.js';
import { SecretFieldsPage } from '../secret-fields/SecretFieldsPage.js';
import { ServiceTokensPage } from '../service-tokens/ServiceTokensPage.js';

type SecurityTab = 'service-tokens' | 'platform-values' | 'definitions';

const TABS: TabItem<SecurityTab>[] = [
  { value: 'service-tokens', label: 'Service tokens' },
  { value: 'platform-values', label: 'Platform values' },
  { value: 'definitions', label: 'Definitions' },
];

const VALID = new Set<string>(TABS.map((t) => t.value));

export function SecurityPage() {
  // The legacy /service-tokens URL redirects here with a ?tab=, so a bookmark
  // still lands where it used to.
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('tab');
  const [tab, setTab] = useState<SecurityTab>(
    fromUrl && VALID.has(fromUrl) ? (fromUrl as SecurityTab) : 'service-tokens',
  );

  function select(next: SecurityTab) {
    setTab(next);
    setParams(next === 'service-tokens' ? {} : { tab: next }, { replace: true });
  }

  // The rate card moved to Cost (Feature 70); a bookmark still lands on it.
  if (fromUrl === 'rate-card') return <Navigate to="/cost?tab=rate-card" replace />;

  return (
    <ListShape
      title="Security"
      subtitle="Credentials and secret definitions, ordered by how often you touch them."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <Tabs items={TABS} value={tab} onChange={select} style={{ marginBottom: 16 }} />

        {tab === 'service-tokens' && <ServiceTokensPage />}
        {tab === 'platform-values' && <PlatformValuesTab />}
        {tab === 'definitions' && <SecretFieldsPage />}
      </div>
    </ListShape>
  );
}

/**
 * The values behind the definitions. Its own component rather than a flag on
 * SecretFieldsPage: the two tabs were sharing one file only because they share
 * one query, and the flag made half of that file unreachable on either tab.
 */
function PlatformValuesTab() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Platform values"
        subtitle="Platform-wide fallback for $secret references. An organization's own value for the same key wins at task time."
      />
      <GlobalSecretsCard />
    </div>
  );
}
